<template>
  <a-modal
    :visible="true"
    width="440px"
    centered
    :body-style="{ padding: 0 }"
    :mask-closable="false"
    :keyboard="false"
    @cancel="handleConfirmationClose"
    v-if="record.status === UserStatus.active"
  >
    <template v-slot:title>
      <span> Editar base </span>
    </template>
    <a-form class="form" layout="vertical" ref="form" :model="formState" :rules="rules">
      <a-form-item name="name">
        <p>Nombre de la base</p>
        <a-auto-complete
          v-model:value="formState.name"
          placeholder="Escribir nombre de la base"
          @input="showConfirmModal = true"
          @search="handleSearchName"
          :class="{ 'ant-form-item-has-error': isNameDuplicated }"
        />
        <span v-if="isNameDuplicated" class="form__error">Este nombre ya esta en uso</span>
      </a-form-item>
      <a-form-item name="entity" v-if="typeBusiness === TypeBusiness.contactCenter">
        <p>Selecciona una empresa</p>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.entity"
          placeholder="Selecciona una empresa"
          disabled
        >
          <a-select-option v-for="entity in entities" :key="entity.id" :value="entity.id">
            {{ entity.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item name="portfolio">
        <p>Selecciona una campaña</p>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.portfolio"
          placeholder="Seleciona una campaña"
          disabled
        >
          <a-select-option
            v-for="portfolio in portfolios"
            :key="portfolio.id"
            :value="portfolio.id"
          >
            {{ portfolio.name }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <p class="space">Selecciona rango de fechas</p>

      <a-row :gutter="24">
        <a-col span="12">
          <a-form-item label="Desde" name="date_start">
            <a-date-picker
              v-model:value="formState.date_start"
              :format="humanDateFormat"
              placeholder="-- / -- / --"
              :show-today="false"
              @change="showConfirmModal = true"
              :disabled-date="disabledDate"
            />
          </a-form-item>
        </a-col>
        <a-col span="12">
          <a-form-item label="Hasta" name="date_finish">
            <a-date-picker
              v-model:value="formState.date_finish"
              :format="humanDateFormat"
              placeholder="-- / -- / --"
              :show-today="false"
              @input="showConfirmModal = true"
              :disabled-date="disabledEndDate"
            />
          </a-form-item>
        </a-col>
      </a-row>
      <div class="container__expire">
        <p class="space">Acciones al caducar tu base</p>
        <a-form-item class="switch--small" name="enable_regeneration">
          <a-checkbox v-model:checked="formState.enable_regeneration" size="small" />
          Habilitar la regeneración de esta base
          <a-tooltip>
            <template v-slot:title>
              Se reactivará esta base desde su fecha de finalización y se utilizará el mismo
              intervalo de tiempo establecido en esta base.
            </template>
            <InfoCircleOutlined />
          </a-tooltip>
        </a-form-item>

        <a-form-item name="assignment_type">
          <a-radio-group
            v-model:value="formState.enable_still_tipificactions"
            @change="showConfirmModal = true"
            :disabled="!formState.enable_regeneration"
          >
            <a-tooltip v-if="!formState.enable_regeneration">
              <template v-slot:title>
                Para habilitar esta opción, debe activar la opción superior
              </template>
              <a-radio :value="false" :style="radioStyle" class="separator__radio">
                Regenerar clientes en blanco (Sin resultados generados por tipificaciones)
              </a-radio>
            </a-tooltip>
            <a-radio
              :value="false"
              :style="radioStyle"
              class="separator__radio"
              v-if="formState.enable_regeneration"
            >
              Regenerar clientes en blanco (Sin resultados generados por tipificaciones)
            </a-radio>
            <div class="separator__group"></div>
            <a-radio :value="true" :style="radioStyle" class="separator__radio">
              Regenerar clientes manteniendo los resultados generados por tipificaciones
            </a-radio>
          </a-radio-group>
        </a-form-item>
      </div>
    </a-form>
    <!-- Fotter Modal -->
    <template v-slot:footer>
      <a-button key="back" @click="handleConfirmationClose"> Cancelar </a-button>
      <a-button key="submit" type="primary" @click="handleOk"> Guardar </a-button>
    </template>
  </a-modal>
</template>

<script>
import { humanDateFormat } from "@/app/shared/utils/constants";
import { stringToDate } from "@/app/shared/utils/dates";
import { defineComponent, ref, reactive, toRaw, createVNode } from "vue";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { UserStatus } from "@/app/shared/utils/enums";
import { TypeBusiness } from "@/app/shared/utils/enums";
import { Modal } from "ant-design-vue";
import { disabledDate } from "@/app/shared/utils/dates";

const MIN_LETTERS_TO_SEARCH = 0;

export default defineComponent({
  components: {
    InfoCircleOutlined,
  },
  props: {
    record: {
      type: Object,
      required: true,
    },
    typeBusiness: {
      type: Number,
      required: true,
    },
  },
  setup(props, context) {
    const form = ref();
    const showConfirmModal = ref(false);
    const formState = reactive({
      name: props.record.name,
      entity: props.record.entity.id,
      portfolio: props.record.portfolio.id,
      date_start: stringToDate(props.record.date_start),
      date_finish: stringToDate(props.record.date_finish),
      email: props.record.email,
      description: props.record.description,
      enable_regeneration: props.record.enable_regeneration,
      enable_still_tipificactions: props.record.enable_still_tipificactions,
    });
    const rules = {
      name: [{ required: true, message: "Campo requerido" }],
      entity: [{ required: true, message: "Campo requerido" }],
      portfolio: [{ required: true, message: "Campo requerido" }],
      date_start: [{ type: "object", required: true, message: "Campo requerido" }],
      date_finish: [{ type: "object", required: true, message: "Campo requerido" }],
      enable_regeneration: [],
      enable_still_tipificactions: [],
    };
    const handleConfirmationClose = () => {
      if (showConfirmModal.value) {
        Modal.confirm({
          title: "¿Estás seguro de descartar cambios?",
          content: "Al cerrar no se guardarán los datos previamente ingresados",
          icon: createVNode(QuestionCircleOutlined),
          onOk: () => handleCancel(),
          centered: true,
          okText: "Sí, descartar cambios",
          cancelText: "No, seguir editando",
        });
      } else {
        handleCancel();
      }
    };
    const handleCancel = () => {
      context.emit("onCloseModal");
      showConfirmModal.value = false;
    };
    return {
      form,
      formState,
      rules,
      handleConfirmationClose,
      showConfirmModal,
      handleCancel,
    };
  },
  data() {
    return {
      UserStatus,
      TypeBusiness,
      bodyStyle: {
        padding: "20px 30px",
        display: "flex",
        justifyContent: "center",
        height: "600px",
      },
      config: {
        rules: [{ type: "object", required: true, message: "Please select time!" }],
      },
      humanDateFormat,
    };
  },
  computed: {
    entities() {
      return this.$store.getters["getEntityList"];
    },
    portfolios() {
      return this.$store.getters["getFilteredPortfolios"];
    },
    getAllBases() {
      return this.$store.getters["getAllBases"];
    },
    isNameDuplicated() {
      return (
        this.getAllBases?.length > 0 &&
        this.getAllBases[0].name === this.formState.name.toLowerCase()
      );
    },
  },
  methods: {
    disabledDate(current) {
      this.formState.date_finish = "";
      return disabledDate(current);
    },
    disabledEndDate(endValue) {
      if (!endValue || !this.formState.date_start) {
        return disabledDate(endValue);
      }
      return this.formState.date_start.valueOf() >= endValue.valueOf();
    },
    handleOk() {
      this.form.validateFields().then(() => {
        this.$emit("onCloseModal");
        let values = toRaw(this.formState);
        const data = { ...values, number_clients: 0 };
        this.$store.dispatch("updateBase", {
          baseId: this.record.id,
          newBase: data,
        });
        this.$message.success(`Se han guardado los cambios correctamente`);
        this.form.resetFields();
      });
    },
    handleSearchName(data) {
      if (data.length > MIN_LETTERS_TO_SEARCH) {
        this.handleFetchBase(data);
      } else {
        this.$store.dispatch("clearInputBase");
      }
    },
    async handleFetchBase(data) {
      await this.$store.dispatch("fetchListBases", data);
    },
  },
});
</script>

<style lang="sass" scoped>
.form
  display: block
  padding: 20px 0
  max-width: 340px
  margin: 0 auto

.space
  margin-bottom: 8px
  margin-top: 20px

.small
  max-height: 20px

.ant-btn
  border-radius: 0px !important
.ant-form-item
  margin-bottom: 12px
p
  margin-bottom: 2px
  font-weight: 600 !important
  color: $gray-9
.flag
  width: 20px
  margin-bottom: 2px
.textdisabled
  color: $gray-6

.container__expire
  margin-bottom: 15px

.switch--small
  margin-bottom: 4px

.separator__radio
  white-space: pre-line
  line-height: 20px !important
  display: flex !important
  margin-top: 10px
  align-items: center !important
  margin-bottom: 10px

.separator__group
  margin-bottom: 20px

.form__error
  color: $red-6
  margin: 0
</style>
